import React, { useState, useEffect } from 'react'
import { OmList, OmListItem, OmCheckbox, OmDropdownMenu, type OmMenuItem } from '@owma/kit'

import { NameObj, NameSplitter } from 'pages/contacts/utils/NameSplitter'
import PerfectScrollbar from 'libs/react-perfect-scrollbar'
import { EmptyData } from 'utils/ui'

import classNames from 'classnames'

import { getAvatarURL } from 'pages/main/data/avatarService'
import { getInitialAvatarSrc, showResult } from 'utils/ui'

import contactHelper from 'pages/contacts/components/SharedContacts/Helper'

import styles from './style.module.scss'

import i18n from 'i18n'
import { confirmDelete } from 'utils/ui'

interface ListEmailItemProps {
  name: string
  email: string
  checkBoxElem?: React.ReactNode
}

function ListEmailItem(props: ListEmailItemProps) {
  const { name, email, checkBoxElem } = props

  const [imgSrc, setImgSrc] = useState(getInitialAvatarSrc(name, email))

  useEffect(() => {
    getAvatarURL(name, email).then((url: any) => {
      setImgSrc(url)
    })
  }, [name, email])

  const imgCls = { invisible: imgSrc == null }

  return (
    <React.Fragment>
      <div className={styles.icon}>
        <img className={classNames(styles.avatar, imgCls)} src={imgSrc} alt="avatar" />
      </div>
      <div className={styles.info}>
        <div className={styles.name}>{name}</div>
        <div className={styles.email}>{email}</div>
      </div>
      {checkBoxElem}
    </React.Fragment>
  )
}

interface ListItemProps {
  name: string | null
  email: string | null
  phone: string | null
  item: any
  checkBoxElem?: React.ReactNode
  organizationElem?: React.ReactNode
  isEditing: boolean
}

function ListItem(props: ListItemProps) {
  const { name, email, phone, item, checkBoxElem, organizationElem, isEditing } = props
  const calcName = name || phone || i18n.t('contact.menu.noName')
  const [imgSrc, setImgSrc] = useState(getInitialAvatarSrc(name, email, { emptyName: calcName }))

  useEffect(() => {
    if (item.photo?.url != null) {
      setImgSrc(item.photo?.url)
    } else {
      getAvatarURL(name, email, false).then((url: any) => {
        if (url != null) {
          setImgSrc(url)
        }
      })
    }
  }, [item.photo?.url, name, email])

  const displayName = name || email || phone || i18n.t('contact.menu.noName')

  return (
    <React.Fragment>
      <div
        className={classNames(
          styles.icon,
          styles.hoverHidden,
          isEditing ? styles.avatarHidden : '',
        )}
      >
        {checkBoxElem}
        <img
          className={styles.avatar}
          src={imgSrc}
          alt="avatar"
          onError={() => {
            const imgVal = getInitialAvatarSrc(name, email, { emptyName: calcName })
            setImgSrc(imgVal)
          }}
        />
      </div>
      <div className={styles.info}>
        <div className={styles.nameContainer}>
          <div className={styles.name}>{displayName}</div>
          {item.recent && <div className={styles.dot}>&nbsp;</div>}
        </div>
        {organizationElem}
      </div>
    </React.Fragment>
  )
}

interface ContactListProps {
  innerRef?: React.MutableRefObject<any>
  initSearchText?: string
  type: string
  category?: string
  enableSelect?: boolean
  enableCheck?: boolean
  emailMode?: boolean
  onSelectedChange?: (id: any, type: string, item: any) => void
  onSelectChanged?: (emails: string[], users: any[]) => void
  loading?: boolean
  size?: string
  disableEmails?: string[]
  excludeEmailList?: string[]
  defaultEmailList?: string[]
  onDuplicate?: (item: any, target: string) => void
  onMoveToPersonal?: (items: any[]) => void
  onExport?: (items: any[]) => void
  disabledEditContact?: boolean
  categoryName?: string
  onCheckedChange?: (checkedKeys: string[]) => void
}

interface ContactListState {
  viewContactList: any[]
  selectedKey: any
  checkedKeys: any[]
  totalCount?: number
}

class ContactList extends React.Component<ContactListProps, ContactListState> {
  state: ContactListState = {
    viewContactList: [],
    selectedKey: null,
    checkedKeys: [],
  }

  selectedKey: any = null
  checkedKeys: any[] = []

  contactList: any[] = []

  contactMap: Record<string, any> = {}
  contactEmailMap: Record<string, any[]> = {}

  scrollRef = React.createRef<any>()

  isListFull = false

  offset = 0

  limit = 0

  disableEmails: string[] = this.props.disableEmails?.map((email) => email.toLowerCase()) || []

  searchText?: string
  searchTimer?: any
  skipScrollTop?: number

  listener = () => {
    setTimeout(() => {
      this.initList(this.searchText)

      if (this.scrollRef.current) {
        this.scrollRef.current.element.scrollTop = 0
        this.skipScrollTop = 0
      }
    }, 500)
  }

  componentDidMount() {
    const { innerRef, initSearchText } = this.props

    if (innerRef != null) {
      innerRef.current = this.createRefObj()
    }

    this.initList(initSearchText)

    contactHelper.addContactChangeListener(this.listener)
  }

  componentDidUpdate(prevProps: ContactListProps) {
    const { type, category, initSearchText } = this.props

    if (prevProps != null && (prevProps.type !== type || prevProps.category !== category)) {
      this.initList(initSearchText)
    }
  }

  componentWillUnmount() {
    contactHelper.removeContactChangeListener(this.listener)
  }

  createRefObj = () => {
    const obj = {
      onSearch: (text: string) => {
        return this.onSearch(text)
      },
      onChange: (e: any) => {
        return this.onChange(e)
      },
      reload: () => {
        this.initList(this.searchText)
      },
      getCheckedIds: () => {
        return this.checkedKeys
      },
      getCheckedItems: () => {
        return this.getCheckedItems()
      },
      selectItem: (id: any, item: any) => {
        this.onItemClick(id, item)
      },
      setFavoriteChecked: (on: boolean) => {
        this.handleFavorite(this.getCheckedItems(), on)
      },
      unselectAll: () => {
        this.handleUnselectAll()
      },
    }

    return obj
  }

  initList = (searchText?: string) => {
    const { type, category } = this.props

    console.log('initList - ', type, category)

    this.offset = -20
    this.limit = 20
    this.isListFull = false

    this.selectedKey = null

    this.contactList = []
    this.contactMap = {}
    this.contactEmailMap = {}

    this.checkedKeys = []
    this.setState({
      checkedKeys: [],
    })
    this.props.onCheckedChange?.(this.checkedKeys)

    this.searchText = searchText

    this.loadMoreData()
  }

  apiCall = async (params: any, fullResponse?: boolean) => {
    const { type, category } = this.props
    let res = null
    if (type === 'myFavorites') {
      res = await contactHelper.favoriteList(params)
    } else {
      const paramsExt: any = {
        ...params,
        brief: fullResponse !== true,
      }
      switch (type) {
        case 'aiContacts':
          paramsExt.ai = 1
          break
        case 'recycleBin':
          paramsExt.deleted = 1
          break
        case 'category':
          paramsExt.category = category
          break
        case 'all':
        default:
          break
      }
      res = await contactHelper.contactList(paramsExt)
    }

    return res
  }

  loadMoreData = () => {
    const { enableSelect, emailMode } = this.props

    this.offset += this.limit

    const params: any = {
      offset: this.offset,
      limit: this.limit,
    }

    if (this.searchText != null && this.searchText.trim().length > 0) {
      params.fsearch = this.searchText
    }

    this.apiCall(params).then((res: any) => {
      console.log('loadData - ', res)
      const { data, total } = res.data

      // map에 저장.
      let newList: any[] = []
      if (emailMode === true) {
        const { excludeEmailList } = this.props

        data.forEach((item: any) => {
          const { person, emails, photo } = item

          let name = null
          if (person) {
            const nameObj = new NameObj({
              prefix: person.prefix,
              first_name: person.first_name,
              last_name: person.last_name,
              middle_name: person.middle_name,
              suffix: person.suffix,
            })
            const nameSplitter = new NameSplitter(navigator.language || 'en-US')
            nameSplitter.guessFullNameStyle(nameObj)
            name = nameSplitter.joinWithName(nameObj, true, true)
          }

          emails.forEach((emailItem: any) => {
            const { email } = emailItem
            const id = `${email}-${item.id}`

            if (excludeEmailList == null || excludeEmailList.indexOf(email) < 0) {
              if (this.contactMap[id] == null) {
                const newItem = {
                  id: id,
                  name: name,
                  email: email,
                  avatarUrl: photo != null && photo.url != null ? photo.url : null,
                }

                this.contactMap[id] = newItem
                newList.push(newItem)

                if (this.contactEmailMap[email] == null) {
                  this.contactEmailMap[email] = []
                }
                this.contactEmailMap[email].push(newItem)
              }
            }
          })
        })
      } else {
        newList = data.filter((item: any) => {
          if (this.contactMap[item.id] == null) {
            this.contactMap[item.id] = item
            return true
          } else {
            console.log('---- already exist - ', item.id, item)
            console.log('---- prev Item - ', this.contactMap[item.id])
          }
          return false
        })
      }

      this.contactList = [...this.contactList, ...newList]

      const newState: any = {
        totalCount: total,
        viewContactList: this.contactList,
      }

      if (enableSelect === true) {
        if (this.contactList.length > 0 && this.selectedKey == null) {
          this.selectedKey = this.contactList[0].id
          newState.selectedKey = this.selectedKey

          this.onItemClick(this.selectedKey, this.contactList[0])
        } else if (this.contactList?.length === 0) {
          this.selectedKey = null
          this.onItemClick(null, null)
        }
      }

      if (emailMode === true) {
        const { defaultEmailList } = this.props
        if (defaultEmailList != null && defaultEmailList.length > 0) {
          this.checkedKeys = [...this.contactList]
            .filter((item) => {
              return defaultEmailList.indexOf(item.email) > -1
            })
            .map((item) => {
              return item.id
            })
          newState.checkedKeys = [...this.checkedKeys]
          this.props.onCheckedChange?.(this.checkedKeys)
        }
      }

      this.setState(newState)

      if (total > this.offset + this.limit) {
        this.checkScreenFull(
          () => {
            this.loadMoreData()
          },
          () => {
            console.log('end loading')
          },
        )
      }
    })
  }

  onSearch = (value: string) => {
    this.initList(value)
  }

  onChange = (e: any) => {
    console.log('onChange - ', e)
    const { value } = e.target
    if (this.searchTimer != null) {
      clearTimeout(this.searchTimer)
    }

    this.searchTimer = setTimeout(() => {
      this.searchTimer = null
      this.onSearch(value)
    }, 300)
  }

  onScrollY = (e: any) => {
    const { loading } = this.props

    // console.log('- onScrollY : ', e)

    // resize로 인해 스크롤 위치가 조정된 상태이므로, 스크롤 처리를 하지 않음.
    if (this.skipScrollTop === e.target.scrollTop) {
      this.skipScrollTop = -1
      return
    }

    if (this.isScrolledByResize === true) {
      this.isScrolledByResize = false
      return
    }

    if (loading === true) {
      // 로딩 중이면 스크롤 처리 안함.
      return
    }

    if (e.target.scrollHeight - e.target.scrollTop < e.target.offsetHeight + 1) {
      this.loadMoreData()
    }
  }

  isScrolledByResize?: boolean

  onYReachStart = (/* e, data */) => {
    // const { parentView } = this.props
    // console.log('onYReachStart : ', e, data)
    // this.loadMoreData()
  }

  checkScreenFull = (onNeedMoreFunc?: () => void, onLoadedFullFunc?: () => void) => {
    setTimeout(() => {
      console.log('scrollRef : ', this.scrollRef)
      if (this.scrollRef.current?.element != null) {
        const elem = this.scrollRef.current.element
        // if (elem.scrollHeight - elem.scrollTop > elem.offsetHeight) {
        //   elem.scrollTop = elem.scrollHeight - elem.offsetHeight
        //   this.scrollRef.current.update()
        // }

        // this.scrollUpdateInner()

        // 기본 화면 높이보다 content가 적으면 다음 페이지 요청.
        if (this.isListFull !== true) {
          if (elem.scrollHeight <= elem.offsetHeight) {
            if (onNeedMoreFunc != null) {
              onNeedMoreFunc()
            }
          } else {
            this.isListFull = true
            if (onLoadedFullFunc != null) {
              onLoadedFullFunc()
            }
          }
        }
      }
    }, 100)
  }

  onItemClick = (id: any, item: any) => {
    const { enableSelect, onSelectedChange, type, emailMode } = this.props
    console.log('onItemClick - ', id, item)

    if (enableSelect === true) {
      this.setState({
        selectedKey: id,
      })

      if (onSelectedChange != null) {
        onSelectedChange(id, type, item)
      }
    }

    if (emailMode === true) {
      this.toggleChecked(id)
    }
  }

  toggleCheckedRange = (id: any) => {
    if (this.checkedKeys.includes(id) === true) {
      this.toggleChecked(id)
      return
    }

    const lastItemId = this.checkedKeys[this.checkedKeys.length - 1]
    const lastItemIdx = this.state.viewContactList.findIndex((item) => {
      if (item.id === lastItemId) {
        return true
      }
      return false
    })
    const newItemIndex = this.state.viewContactList.findIndex((item) => {
      if (item.id === id) {
        return true
      }
      return false
    })

    let startIndex = null
    let endIndex = null
    if (newItemIndex > lastItemIdx) {
      startIndex = lastItemIdx + 1
      endIndex = newItemIndex
    } else if (newItemIndex < lastItemIdx) {
      startIndex = newItemIndex
      endIndex = lastItemIdx - 1
    } else {
      return
    }

    for (let i = startIndex; i <= endIndex; i++) {
      const item = this.state.viewContactList[i]
      if (this.checkedKeys.includes(item.id) === true) {
        continue
      }
      this.checkedKeys.push(item.id)
    }

    this.setState({
      checkedKeys: [...this.checkedKeys],
    })
    this.props.onCheckedChange?.(this.checkedKeys)
  }

  toggleChecked = (id: any) => {
    const { enableCheck, onSelectChanged, emailMode } = this.props
    if (enableCheck === true) {
      const index = this.checkedKeys.indexOf(id)
      if (emailMode === true) {
        const { email } = this.contactMap[id]
        const emailList = this.contactEmailMap[email]
        if (emailList != null && emailList.length > 0) {
          emailList.forEach((emailItem) => {
            const pos = this.checkedKeys.indexOf(emailItem.id)
            if (index > -1 && pos > -1) {
              this.checkedKeys.splice(pos, 1)
            } else if (index < 0 && pos < 0) {
              this.checkedKeys.push(emailItem.id)
            }
          })
        }
      } else {
        if (index > -1) {
          this.checkedKeys.splice(index, 1)
        } else {
          this.checkedKeys.push(id)
        }
      }

      this.setState({
        checkedKeys: [...this.checkedKeys],
      })
      this.props.onCheckedChange?.(this.checkedKeys)

      if (onSelectChanged != null) {
        const checkedEmails: string[] = []
        const checkedUsers: any[] = []
        this.checkedKeys.forEach((key) => {
          if (emailMode === true) {
            const user = this.contactMap[key]
            if (user != null && checkedEmails.indexOf(user.email) < 0) {
              checkedEmails.push(user.email)
              checkedUsers.push(user)
            }
          } else {
            const user = this.contactMap[key]
            if (user != null && checkedEmails.indexOf(user.account) < 0) {
              const email =
                user.emails != null && user.emails.length > 0 ? user.emails[0].email : null
              checkedEmails.push(email)
              checkedUsers.push(user)
            }
          }
        })

        onSelectChanged(checkedEmails, checkedUsers)
      }
    }
  }

  getCheckedItems = () => {
    return this.checkedKeys.map((id) => this.contactMap[id]).filter((item) => item != null)
  }

  handleFavorite = (items: any[], on: boolean) => {
    console.log('handleFavorite - ', items, on)
    Promise.all(items.map((item) => contactHelper.setFavorite(item.id, on))).then((res) => {
      console.log('handleFavorite - ', res)
    })
  }

  handleCopyLink = (item: any) => {
    console.log('handleCopyLink - ', item)
    const url = `${window.location.origin}/contacts?type=shared&id=${item.id}`
    navigator.clipboard.writeText(url)
    showResult({
      key: 'copyLink',
      loading: false,
      closeOnClick: true,
      message: i18n.t('contact.desc.copyLink'),
    })
  }

  _runDelete = (items: any[], isPermanent: boolean) => {
    Promise.all(items.map((item) => contactHelper.deleteContact(item.id, isPermanent))).then(
      (res) => {
        console.log('handleDelete - ', res)
        if (isPermanent === false && items.length === 1) {
          const [item] = items
          showResult({
            key: 'deleteContact',
            loading: false,
            closeOnClick: true,
            message: i18n.t('contact.desc.undoMessage'),
            actionText: i18n.t('common.button.undo'),
            action: () => {
              contactHelper.restoreContact(item.id).then((res) => {
                console.log('handleRecover - ', res)
              })
            },
          })
        }
      },
    )
  }

  handleDelete = (items: any[]) => {
    console.log('handleDelete - ', items)
    if (this.props.type === 'recycleBin') {
      confirmDelete({
        title: i18n.t('contact.menu.deleteContact'),
        content:
          items.length > 1
            ? i18n.t('contact.menu.deleteContactsDesc', { count: items.length })
            : i18n.t('contact.menu.deleteContactDesc'),
        okText: i18n.t('common.button.ok'),
        cancelText: i18n.t('common.button.cancel'),
        onOk: () => {
          this._runDelete(items, true)
        },
      })
    } else {
      this._runDelete(items, false)
    }
  }

  handleRecover = (items: any[]) => {
    console.log('handleRecover - ', items)
    Promise.all(items.map((item) => contactHelper.restoreContact(item.id))).then((res) => {
      console.log('handleRecover - ', res)
    })
  }

  renderItem = (item: any) => {
    const { selectedKey } = this.state
    const { enableSelect, enableCheck, emailMode, type } = this.props
    const { id } = item

    const clsProps: any = {}

    // 멀티 셀렉트 중에는 상세 영역이 액션 목록으로 전환되어 단일 선택이 의미를 잃는다.
    // 이때 이전 선택 배경을 남겨두면 체크된 행과 구분되지 않으므로 끈다.
    if (enableSelect === true && this.checkedKeys.length === 0) {
      clsProps.selected = selectedKey === id
    }

    if (enableCheck === true && this.checkedKeys.indexOf(id) > -1) {
      clsProps.checked = true
    }

    let checkBoxElem = null
    if (enableCheck === true) {
      const checkBoxAttrs = {
        checked: this.checkedKeys.indexOf(id) > -1,
      }

      checkBoxElem = (
        <div
          className={classNames(styles.check, emailMode ? '' : styles.userCheck)}
          onClick={(e) => {
            if (emailMode !== true) {
              e.stopPropagation()
              e.preventDefault()
              if (e.shiftKey === true) {
                this.toggleCheckedRange(id)
              } else {
                this.toggleChecked(id)
              }
            }
          }}
        >
          <span
            onClick={(e) => {
              e.stopPropagation()
              if (emailMode === true) {
                this.onItemClick(id, item)
              } else {
                if (e.shiftKey === true) {
                  this.toggleCheckedRange(id)
                } else {
                  this.toggleChecked(id)
                }
              }
            }}
          >
            <OmCheckbox {...checkBoxAttrs} disabled={this.disableEmails.indexOf(item.email) > -1} />
          </span>
        </div>
      )
    }

    if (emailMode === true) {
      const { name, email } = item

      return (
        <OmListItem
          key={id}
          className={classNames(styles.contactItem, clsProps)}
          onClick={() => {
            if (this.disableEmails.indexOf(item.email) > -1) {
              return
            }
            this.onItemClick(id, item)
          }}
        >
          <ListEmailItem name={name} email={email} checkBoxElem={checkBoxElem} />
        </OmListItem>
      )
    }

    const { person, organization, emails /* , phones */ } = item

    let name = null
    if (person) {
      const nameObj = new NameObj({
        prefix: person.prefix,
        first_name: person.first_name,
        last_name: person.last_name,
        middle_name: person.middle_name,
        suffix: person.suffix,
      })
      const nameSplitter = new NameSplitter(navigator.language || 'en-US')
      nameSplitter.guessFullNameStyle(nameObj)
      name = nameSplitter.joinWithName(nameObj, true, true)
    }
    let email = null
    if (emails != null && emails.length > 0) {
      email = emails[0].email
    }
    let phone = null
    if (item.phones != null && item.phones.length > 0) {
      phone = item.phones[0].number
    }

    let organizationElem = null
    if (organization != null) {
      const { company, job_title: jobTitle, department } = organization
      const orgText = [company, jobTitle, department]
        .filter((item) => item != null && item.trim().length > 0)
        .join(' • ')
      organizationElem = <div className={styles.organization}>{orgText}</div>
    }

    const isTrash = type === 'recycleBin'
    // 우클릭 대상이 이미 선택집합에 포함된 경우에만 멀티 메뉴를 띄우고 선택된 전체 항목에 적용한다.
    // 선택집합에 포함되지 않은 항목을 우클릭하면 멀티 셀렉트를 해제하고 단일 항목 메뉴를 띄운다.
    const groupMode = this.checkedKeys.length > 0 && this.checkedKeys.indexOf(id) > -1
    const checkedItems = groupMode ? this.getCheckedItems() : []
    const targetItems = checkedItems.length > 0 ? checkedItems : [item]

    const contextMenuItems: OmMenuItem[] = !isTrash
      ? [
          ...(groupMode
            ? [
                ...(type !== 'myFavorites'
                  ? [
                      {
                        key: 'favoriteAdd',
                        label: i18n.t('contact.menu.addfavorite'),
                        onClick: () => {
                          this.handleFavorite(targetItems, true)
                          this.handleUnselectAll()
                        },
                      },
                    ]
                  : []),
                {
                  key: 'favoriteRemove',
                  label: i18n.t('contact.menu.removeFavorite'),
                  onClick: () => {
                    this.handleFavorite(targetItems, false)
                    this.handleUnselectAll()
                  },
                },
              ]
            : [
                {
                  key: 'favorite',
                  label:
                    item.favorite || type === 'myFavorites'
                      ? i18n.t('contact.menu.removeFavorite')
                      : i18n.t('contact.menu.addfavorite'),
                  onClick: () => {
                    const on = item.favorite || type === 'myFavorites' ? false : true
                    this.handleFavorite(targetItems, on)
                  },
                },
                {
                  key: 'copyLink',
                  label: i18n.t('contact.menu.copyLink'),
                  onClick: () => this.handleCopyLink(item),
                },
                {
                  type: 'divider' as const,
                },
                {
                  key: 'duplicate',
                  label: i18n.t('signature.menu.duplicate'),
                  children: [
                    {
                      key: 'duplicateToPublic',
                      label: i18n.t('contact.menu.duplicateToShared'),
                      onClick: () => this.props.onDuplicate?.(item, 'public'),
                    },
                    {
                      key: 'duplicateToPersonal',
                      label: i18n.t('contact.menu.duplicateToPersonal'),
                      onClick: () => this.props.onDuplicate?.(item, 'personal'),
                    },
                  ],
                },
              ]),
          {
            key: 'move',
            label: i18n.t('contact.menu.moveToPersonal'),
            onClick: () => {
              this.props.onMoveToPersonal?.(targetItems)
              if (groupMode) {
                this.handleUnselectAll()
              }
            },
          },
          {
            key: 'export',
            label: i18n.t('contact.menu.export'),
            onClick: () => {
              this.props.onExport?.(targetItems)
              if (groupMode) {
                this.handleUnselectAll()
              }
            },
          },
        ]
      : []

    if (type === 'recycleBin') {
      contextMenuItems.push({
        key: 'recover',
        label: i18n.t('contact.menu.recover'),
        onClick: () => {
          this.handleRecover(targetItems)
          if (groupMode) {
            this.handleUnselectAll()
          }
        },
      })
    }

    if (contextMenuItems.length > 0) {
      contextMenuItems.push({
        type: 'divider',
      })
    }
    contextMenuItems.push({
      key: 'delete',
      label: <span style={{ color: 'red' }}>{i18n.t('common.button.delete')}</span>,
      onClick: () => {
        this.handleDelete(targetItems)
        if (groupMode) {
          this.handleUnselectAll()
        }
      },
      disabled: this.props.disabledEditContact,
    })

    return (
      <OmDropdownMenu
        menu={{ items: contextMenuItems }}
        trigger={['contextMenu']}
        destroyOnHidden
        overlayClassName="contextMenu"
        onOpenChange={(visible) => {
          if (visible === true && groupMode === false && this.checkedKeys.length > 0) {
            this.handleUnselectAll()
          }
        }}
      >
        <OmListItem
          key={id}
          className={classNames(styles.contactItem, clsProps)}
          onClick={(e) => {
            console.log('clicked event', e.shiftKey)
            e.stopPropagation()
            if (this.checkedKeys.length > 0) {
              if (e.shiftKey) {
                this.toggleCheckedRange(id)
              } else {
                this.toggleChecked(id)
              }
            } else {
              this.onItemClick(id, item)
            }
          }}
        >
          <ListItem
            name={name}
            email={email}
            phone={phone}
            item={item}
            checkBoxElem={checkBoxElem}
            organizationElem={organizationElem}
            isEditing={this.checkedKeys.length > 0}
          />
        </OmListItem>
      </OmDropdownMenu>
    )
  }

  getTitle = () => {
    const checkedCount = this.state.checkedKeys.length
    if (checkedCount > 0) {
      return i18n.t('contact.title.selected', { count: checkedCount })
    }
    const { category, categoryName, type } = this.props

    let returnTitle = ''
    if (categoryName) returnTitle = categoryName
    else if (type === 'category' && category === '0')
      returnTitle = i18n.t('contact.tree.noCategory')
    else if (type === 'all') returnTitle = i18n.t('contact.tree.all')
    else if (type === 'myFavorites') returnTitle = i18n.t('contact.tree.myFavorites')
    else if (type === 'recycleBin') returnTitle = i18n.t('contact.tree.recycleBin')
    else if (type === 'aiContacts') returnTitle = i18n.t('contact.tree.aiContacts')

    if (this.state.totalCount && this.state.totalCount > 0)
      returnTitle += ' - ' + i18n.t('contact.title.count', { count: this.state.totalCount })

    return returnTitle
  }

  handleUnselectAll = () => {
    this.checkedKeys = []
    this.setState({ checkedKeys: [] })
    this.props.onCheckedChange?.(this.checkedKeys)
  }

  handleSelectAll = () => {
    this.checkedKeys = this.contactList.map((item) => item.id)
    this.setState({ checkedKeys: [...this.checkedKeys] })
    this.props.onCheckedChange?.(this.checkedKeys)
  }

  // 로드된 항목(this.contactList) 기준 3-state. 서버 totalCount 는 절대 쓰지 않는다 -
  // 그걸 쓰면 전체선택 직후에도 indeterminate 로 보이는 모순이 생긴다.
  getHeaderChecked = (): boolean | null => {
    const loadedCount = this.contactList.length
    const checkedCount = this.checkedKeys.length

    if (loadedCount === 0 || checkedCount === 0) {
      return false
    }
    if (checkedCount === loadedCount) {
      return true
    }
    return null
  }

  handleHeaderCheckToggle = () => {
    if (this.contactList.length === 0) {
      return
    }

    const checked = this.getHeaderChecked()
    if (checked !== true) {
      this.handleSelectAll()
    } else {
      this.handleUnselectAll()
    }
  }

  render() {
    const { viewContactList } = this.state
    const { size, emailMode } = this.props

    const listAttrs = {
      itemLayout: 'horizontal' as const,
      dataSource: viewContactList,
      renderItem: this.renderItem,
      emptyText:
        this.searchText?.length && this.searchText.length > 0 ? (
          <EmptyData type="search" mailbox={null as any} />
        ) : (
          <EmptyData type="contacts" mailbox={null as any} />
        ),
      // loadMore: loadMore
    }

    const clsProps: any = {
      small: size === 'small',
    }

    const checkedCount = this.state.checkedKeys.length
    const title = this.getTitle()

    const headerChecked = this.getHeaderChecked()
    const headerIndeterminate = headerChecked == null

    return (
      <div
        className={classNames(
          styles.contactList,
          'contantList',
          clsProps,
          checkedCount > 0 ? styles.contactListEditMode : '',
        )}
      >
        {emailMode !== true && (
          <div className={styles.listHeader}>
            <div
              className={styles.headerCheck}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                this.handleHeaderCheckToggle()
              }}
            >
              {/* 토글은 바깥 div.headerCheck onClick(버블)이 단일 담당. 체크박스는 controlled 표시 전용. */}
              <OmCheckbox
                checked={headerChecked != null ? headerChecked : false}
                indeterminate={headerIndeterminate}
                onChange={() => {}}
              />
            </div>
            <span className={styles.title}>{title}</span>
          </div>
        )}
        <PerfectScrollbar
          className={styles.scrollView}
          refScroll={this.scrollRef}
          height="100%"
          onScrollY={this.onScrollY}
          onYReachStart={this.onYReachStart}
        >
          <OmList {...listAttrs} />
        </PerfectScrollbar>
      </div>
    )
  }
}

export default ContactList
